import moment from 'moment-timezone';
import { logout } from '/src/features/login/api/login-service.js';
import { setCookie } from '/src/common/cookie-utils';
import { socketService } from '../../src/common/socket/socket.service';

/**
 * @module store/user-login/actions
 */

/**
 * Get the details of current logged in user and set the timezone as
 * mentioned in the user details.
 */
export async function userDetails({ commit }, forOtpValidation = false) {
  try {
    // send screen details from here
    const user = await this.$axios.$get(`/api/users/current`, { timeout: 30000 });

    if (!forOtpValidation) {
      // also call another API to update screen width and height in login history
      this.$axios
        .$post(`/api/users/update-screen-size`, {
          width: window.screen.width,
          height: window.screen.height
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('Screen size update failed, but continuing login', err);
          return null;
        });
    }

    if (user.platFormSettings) {
      user.platFormSettings.ENABLE_QUICK_LINKS = user.platFormSettings.ENABLE_QUICK_LINKS ?? true;
      user.platFormSettings = Object.keys(user.platFormSettings).reduce(
        (acc, current) => ({ ...acc, [current.toUpperCase()]: user.platFormSettings[current] }),
        {}
      );
    }

    commit('setUserDetails', user);

    // make Socket connection
    await socketService.connect();

    const finalTimezone = user.Timezone && user.Timezone.trim() !== '' ? user.Timezone : moment.tz.guess();
    moment.tz.setDefault(finalTimezone);
    setCookie('timezonename', finalTimezone);

    if (user.numberFormat) {
      this.vueApp.$nuxt.$changeNumeralLocale(user.numberFormat);
    }
    const dateFormat = user.dateFormat || 'DD/MM/YYYY';
    const timeFormat = user.EnableMeridiemFormat ? 'hh:mm A' : 'HH:mm';

    setCookie('enableMeridiemFormat', user.EnableMeridiemFormat === true ? 'true' : 'false');
    setCookie('userDateFormat', user.dateFormat);

    moment.defaultFormat = `${dateFormat} ${timeFormat}`;
    if (user.platformTheme) {
      // set the theme here
      // setEnvironmentSpecificStyles({}, user.platformTheme);
      commit('setClientThemeDetails', {
        style: user.platformTheme.style?.sParseJSON({}),
        styleV2: user.platformTheme.styleV2?.sParseJSON({}),
        largeLogo: user.platformTheme.largeLogo,
        largeLogoSwitch: user.platformTheme.largeLogoSwitch,
        logo: user.platformTheme.logo,
        backgroundImage: user.platformTheme.backgroundImg,
        backgroundImgOption: user.platformTheme.backgroundImgOption,
        applied: false
      });
    }
    return user;
  } catch (error) {
    if (error?.response?.status === 500) {
      await logout();
      if (error.response.data?.message === 'User is locked.') {
        window.location = '/login?reason=userLocked';
      } else {
        window.location = '/login?reason=loginagain';
      }
    }
    return null;
  }
}

/**
 * Get the list of user specific active platform notifications
 * @param {object} ctx Vuex context object having commit, state, getters, dispatch etc methods/properties
 */
export async function fetchUserSpecificPlatformNotifications({ commit }) {
  try {
    const list = await this.$axios.$get('/api/userfeed/userSpecific');
    commit('setPlatformNotificationsList', list);
  } catch (error) {
    this.$toast.error(
      this.$errorFromResponse(
        this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: this.vueApp.$nuxt.$i18n.global.t('common.sub_menu.platform_notifications')
        }),
        error
      )
    );
  }
}

/**
 * Get the list of user specific quicklinks
 * @param {object} ctx Vuex context object having commit, state, getters, dispatch etc methods/properties
 */
export async function fetchUserSpecificQuicklinks({ commit }) {
  try {
    const list = await this.$axios.$get('/api/quicklinks');
    commit('setQuicklinks', list);
  } catch (error) {
    this.$toast.error(
      this.$errorFromResponse(
        this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
        }),
        error
      )
    );
  }
}

/**
 * Updates specific quicklink
 * @param {object} ctx Vuex context object having commit, state, getters, dispatch etc methods/properties
 */
export async function updateQuicklink({ commit }, quicklink) {
  try {
    await this.$axios.$put(`/api/quicklinks/${quicklink.UUID}`, quicklink);
    commit('setQuicklink', quicklink);
    this.$toast.success(
      this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.update_success', {
        feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
      })
    );
  } catch (error) {
    this.$toast.error(
      this.$errorFromResponse(
        this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.saving_action', {
          feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
        }),
        error
      )
    );
  }
}

/**
 * Adds a quicklink
 * @param {object} ctx Vuex context object having commit, state, getters, dispatch etc methods/properties
 */
export async function addQuicklink({ commit }, quicklink) {
  try {
    const UUID = await this.$axios.$post('/api/quicklinks', quicklink);
    commit('addQuicklink', { ...quicklink, CreatedAt: new Date(), UUID });
    this.$toast.success(
      this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.create_success', {
        feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
      })
    );
  } catch (error) {
    this.$toast.error(
      this.$errorFromResponse(
        this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.creating_action', {
          feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
        }),
        error
      )
    );
  }
}

/**
 * Removes specific quicklink
 * @param {object} ctx Vuex context object having commit, state, getters, dispatch etc methods/properties
 */
export async function removeQuicklink({ commit }, quicklink) {
  try {
    await this.$axios.$delete(`/api/quicklinks/${quicklink.UUID}`);

    this.$toast.success(
      this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.delete_success', {
        feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
      })
    );

    commit('removeQuicklink', quicklink);
  } catch (error) {
    this.$toast.error(
      this.$errorFromResponse(
        this.vueApp.$nuxt.$i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: this.vueApp.$nuxt.$i18n.global.t('common.quicklinks.title')
        }),
        error
      )
    );
  }
}
